import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { AppCTX } from '../data/AppContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function TeamSignUp({ onBack }) {
  const { login } = React.useContext(AppCTX);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConsumeInvite = async () => {
    setError('');
    setLoading(true);

    try {
      if (!code || !email || !password) {
        setError('Please enter invite code, email, and password.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/invites/consume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inviteCode: code,
          password,
          email, // for alignment, though backend uses email from invite
        }),
      });

      const responseData = await response.json();

      if (!response.ok || !responseData.success) {
        throw new Error(responseData.error || 'Failed to create account.');
      }

      const loginSuccess = await login(
        responseData.session.access_token,
        responseData.session.refresh_token,
        responseData.data,
        responseData.session.expires_in
      );

      if (!loginSuccess) {
        setError('Failed to store session. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Network error occurred. Please try again.');
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100vw',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0b0b0b',
      }}
    >
      {/* Background video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          minWidth: '100%',
          minHeight: '100%',
          width: 'auto',
          height: 'auto',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          objectFit: 'cover',
        }}
      >
        <source src={process.env.PUBLIC_URL + '/bg-video.mp4'} type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.9) 100%)',
          zIndex: 2,
        }}
      />

      {/* Card */}
      <Paper
        elevation={8}
        sx={{
          position: 'relative',
          zIndex: 3,
          width: '90%',
          maxWidth: 420,
          p: 3,
          borderRadius: 3,
          background: 'rgba(18,18,18,0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>
            Enter Your Invite Code
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
            Join your team to get started
          </Typography>

          <TextField
            fullWidth
            label="Invite Code"
            variant="outlined"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{ style: { color: '#fff' } }}
          />

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{ style: { color: '#fff' } }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{ style: { color: '#fff' } }}
          />

          {error && (
            <Typography variant="body2" color="error" sx={{ width: '100%', textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleConsumeInvite}
            disabled={loading || code.length !== 6}
            sx={{
              mt: 1,
              bgcolor: '#fff',
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#e0e0e0' },
            }}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>

          {/* Back to Login Button */}
          {onBack && (
            <Button
              variant="text"
              fullWidth
              sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}
              onClick={onBack}
            >
              Back to Login
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
